import ImageProxy from '@/proxies/Image';
import ProjectsProxy from '@/proxies/Projects';
import FeedbackProxy from '@/proxies/Feedback';
import DataTransform from '@/transforms/FramesData';
import EventsTransform from '@/transforms/Events';
import ProjectsTransform from '@/transforms/Projects';
import StatisticTransform from '@/transforms/Statistic';
import download from '@/utils/download';
import * as types from './mutation-types';
import { CANVAS_CANONICAL, CANVAS_MAIN, CANVAS_SECOND, PLAYERS_ALL_CODE, PLAYER_CODE } from '@/constants';

export default {
  async addFrameFeedback({ dispatch }, payload) {
    const proxy = new FeedbackProxy();
    try {
      await proxy.addFrameFeedback(payload);
      dispatch('notification/success', 'confirmationmessage.add_feedback', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_add_feedback', { root: true });
    }
  },

  self({ commit }, payload) {
    commit(types.SELF, payload);
  },

  addDetect({ commit, dispatch, getters }, payload) {
    const detects = [...getters.detects, payload];
    commit(types.OBJECTS, detects);
    dispatch('setFilteredDetects');
  },

  canvasSize({ commit, getters }, payload) {
    const { width, height } = getters.canvasSize(payload.type);
    if (width !== payload.width || height !== payload.height) {
      commit(types.SET_CANVAS_SIZE, payload);
    }
  },

  deleteNewDetect({ commit, dispatch, getters }) {
    const detects = getters.detects.filter(({ isNew }) => !isNew);

    commit(types.OBJECTS, detects);
    dispatch('setFilteredDetects');
    dispatch('resetDetectEditing');
  },

  async changeDetect({ dispatch }, payload) {
    const { frame_id, job_id, object_type_id, player_name, player_number, track_id } = payload;
    const field = `${frame_id}_changeDetect`;
    dispatch('setLoading', { field });
    try {
      const proxy = new ProjectsProxy();
      const { isNew, ...params } = payload;
      const response = await proxy.changeDetect(params);
      if (response) {
        if (track_id) {
          dispatch('tracksMapping/changeTrack', { player: { player_name }, track_id }, { root: true })
        }
        if (isNew) {
          const newObject = object_type_id === PLAYER_CODE
            ? { object_type_id, player_number, track_id } : null;
          dispatch('setPreviousNewDetect', newObject);
        }
        dispatch('resetDetectEditing');
        await dispatch('getObjects', {
          frame_id,
          job_id,
        });
      }
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_change_detect', { root: true });
    } finally {
      dispatch('setLoading', { field });
    }
  },

  changeOneDetect({ dispatch, getters, rootGetters }, payload) {
    const { player, track_id } = payload;
    if (player.player_id) {
      const pl = rootGetters['players/items'].find(({ id }) => id === player.player_id);
      player.player_name = pl ? pl.player_name : '';
    }
    const newDetects = getters.detectsFiltered.map(item =>
      item.track_id === track_id ? { ...item, player: { ...item.player, ...player } } : item);
    dispatch('setVisible', newDetects);
  },

  async deleteDetect({ dispatch }, payload) {
    if (!payload.length) {
      return
    }

    const { frame_id, job_id } = payload[0];
    const field = `${frame_id}_deleteDetect`;
    dispatch('setLoading', { field });
    const proxy = new ProjectsProxy();
    try {
      const boxes = payload.map(({ id }) => ({ _id: id }));
      const data = { boxes };
      const response = await proxy.deleteDetect(job_id, data);
      dispatch('resetDetectEditing');
      if (response) {
        await dispatch('getObjects', {
          frame_id,
          job_id,
        });
      }
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_delete_detect', { root: true });
    } finally {
      dispatch('setLoading', { field });
    }
  },

  // EVENTS START

  async addDefaultEvents({ dispatch }, jobId) {
    try {
      const proxy = new ProjectsProxy();
      await proxy.addDefaultEvents(jobId);
      await Promise.all([
        dispatch('getEvents', { reload: true }),
        dispatch('tracksMapping/refresh', jobId, { root: true }),
      ]);
      dispatch('notification/success', 'confirmationmessage.events_add_default', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.events_failed_to_add_default', { root: true });
    }
  },

  async createEvent({ dispatch }, payload) {
    const proxy = new ProjectsProxy();
    try {
      await proxy.createEvent(payload);
      await dispatch('getEvents', { reload: true });
      dispatch('notification/success', 'confirmationmessage.event_add', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.event_failed_to_add', { root: true });
    }
  },

  async createEvents({ commit, dispatch }, payload) {
    try {
      await Promise.all(payload.map(el => {
        const proxy = new ProjectsProxy();
        return proxy.createEvent(el);
      }));
      await dispatch('getEvents', { reload: true });
      commit(types.RESET_EVENTS_NEW);
      dispatch('notification/success', 'confirmationmessage.events_add', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.events_failed_to_add', { root: true });
    }
  },

  async deleteEvent({ dispatch }, payload) {
    const proxy = new ProjectsProxy();
    try {
      await proxy.deleteEvent(payload);
      await dispatch('getEvents', { reload: true });
      dispatch('notification/success', 'confirmationmessage.event_delete', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.event_failed_to_delete', { root: true });
    }
  },

  async downloadEvents({ dispatch }, jobId) {
    try {
      const proxy = new ProjectsProxy();
      const { data, headers } = await proxy.getEventsForDownload(jobId);
      const formatedJson = JSON.stringify(data, null, 2);
      const blob = new Blob([formatedJson], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const headerName = headers['content-disposition'] || 'filename=events.json';
      const [_, fileName] = headerName?.split('filename=');
      download(url, fileName)
      dispatch('notification/success', 'confirmationmessage.download_starts_soon', { root: true });
    } catch (e) {
      console.log(e);
      dispatch('notification/error', 'errormessage.failed_to_get_events', { root: true });
    }
  },

  async getEvents({ commit, dispatch, getters }, payload = {}) {
    const page = payload?.page || getters.eventsPage;
    if (payload.reload) {
      commit(types.SET_EVENTS, {
        ...getters.events,
        items: {},
        page,
        total: 0,
      });
    }
    if (getters.eventsItems[page] && !payload.reload) {
      dispatch('setEventsPage', page);
      return;
    }

    try {
      commit(types.SET_EVENTS_LOADING, true);
      const params = {
        job_id: getters.self?.id,
        limit: getters.eventsLimit,
        page,
        ...(getters.eventFilter && { types: getters.eventFilter }),
        ...(getters.eventSearch && { query: getters.eventSearch }),
      }
      const proxy = new ProjectsProxy();
      const { events, total } = await proxy.getEvents(params);
      const result = {
        ...getters.events,
        items: { ...getters.eventsItems, [page]: EventsTransform.fetchCollection(events) },
        page,
        total,
      };
      commit(types.SET_EVENTS, result);
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_events', { root: true });
    } finally {
      commit(types.SET_EVENTS_LOADING, false);
    }
  },

  async getEventTypes({ commit, dispatch, getters }) {
    if (getters.eventTypes.length) {
      return;
    }

    try {
      const proxy = new ProjectsProxy();
      const { events } = await proxy.getEventTypes();
      commit(types.SET_EVENT_TYPES, events);
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_events', { root: true });
    }
  },

  setEventFilter({ commit }, payload) {
    commit(types.SET_EVENT_FILTER, payload);
  },

  setEventSearch({ commit }, payload) {
    commit(types.SET_EVENT_SEARCH, payload);
  },

  setEventsNew({ commit, getters }, payload) {
    const events = getters.eventsNew;
    const lastIndex = events.length - 1;
    const eventsNew = payload.firstFrame
      ? [ ...events, payload ]
      : events.map((el, i) => i === lastIndex ? { ...el, ...payload } : el);
    commit(types.SET_EVENTS_NEW, eventsNew);
  },

  async updateEvent({ dispatch }, payload) {
    const proxy = new ProjectsProxy();
    try {
      await proxy.updateEvent(payload);
      await dispatch('getEvents', { reload: true });
      dispatch('notification/success', 'confirmationmessage.event_update', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.event_failed_to_update', { root: true });
    }
  },

  // EVENTS END

  async deleteLogs({ dispatch }, payload) {
    const proxy = new ProjectsProxy();
    try {
      await proxy.deleteLogs(payload);
      await dispatch('getLogsForProject');
      dispatch('notification/success', 'confirmationmessage.logs_delete', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_delete_logs', { root: true });
    }
  },

  async getLogsForProject({ commit, dispatch, getters }) {
    try {
      commit(types.SET_LOGS_LOADING, true);
      const prevItems = getters.logsItems;
      const proxy = new ProjectsProxy();
      const { logs } = await proxy.getTaskStatusesForProject(getters.self?.id);
      const items = ProjectsTransform.fetchStatuses(logs);
      commit(types.SET_LOGS, { ...getters.logs, items });

      // Show notification when task status is finished
      if (!prevItems.length) {
        return
      }
      const dif = items.filter(el2 => {
        const res = !prevItems.some((el1 => (`${el2.date}-${el2.status}` === `${el1.date}-${el1.status}`)));
        return res ? el2.status === 'finished' : res;
      });
      if (!!dif.length) {
        dif.forEach(el => dispatch('notification/success', `${el.game}: ${el.message}`, { root: true }));
      }
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_logs', { root: true });
    } finally {
      commit(types.SET_LOGS_LOADING, false);
    }
  },

  getLogsLoop({ dispatch, commit, getters }) {
    dispatch('getLogsForProject');
    const prevInterval = getters.logsLoop;
    if (prevInterval) {
      clearInterval(prevInterval);
      dispatch('notification/success', 'confirmationmessage.logs_restarted', { root: true });
    }

    const newInterval = setInterval(() => {
      if (getters.logsLoop) {
        dispatch('getLogsForProject');
      } else {
        clearInterval(newInterval);
      }
    }, 10000);
    commit(types.SET_LOGS_LOOP, newInterval);
  },

  async getPassScheme({ commit, dispatch, getters }) {
    try {
      const job = getters?.self?.id;
      const frame = getters?.currentFrame;
      commit(types.SET_EVENT_LOADING, true);
      const proxy = new ProjectsProxy();
      const response = await proxy.getBallData(job, frame);
      const result = {
        ...EventsTransform.fetchEvent(response),
        loading: false,
      }
      commit(types.SET_EVENT, result);
    } catch (e) {
      commit(types.SET_EVENT_LOADING, false);
      dispatch('notification/error', 'errormessage.failed_to_get_ball_data', { root: true });
    }
  },

  async getFrameStatistic({ commit, dispatch, getters }) {
    const frameId = getters.currentFrame;
    if (frameId === getters.statistic.frame?.id) {
      return
    }

    const type = CANVAS_CANONICAL;
    const jobId = getters.self?.id;
    const field = `${frameId}_getStatistic`;
    dispatch('setLoading', { type, field });

    try {
      const proxy = new ProjectsProxy();
      const frame = await proxy.getStatisticFrame(jobId, frameId);
      const data = {
        ...getters.statistic,
        frame: StatisticTransform.frame(frame),
      }
      commit(types.SET_STATISTICS, data);
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_frames_statistic', { root: true });
    } finally {
      dispatch('setLoading', { type, field });
    }
  },

  async getGameStatistic({ commit, dispatch, getters }) {
    if (!!getters.statisticGame?.table.length) {
      return
    }

    try {
      commit(types.SET_GAME_STATISTICS_LOADING, true);
      const proxy = new ProjectsProxy();
      const { data } = await proxy.getStatisticGame(getters.self?.id);
      const payload = {
        ...getters.statistic,
        game: StatisticTransform.game(data),
      }
      commit(types.SET_STATISTICS, payload);
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_game_statistic', { root: true });
    } finally {
      commit(types.SET_GAME_STATISTICS_LOADING, false);
    }
  },

  async getPlayersStatistic({ commit, dispatch, getters }) {
    if (!!getters.statisticPlayers?.items.length) {
      return
    }

    try {
      commit(types.SET_PLAYERS_STATISTICS_LOADING, true);
      const proxy = new ProjectsProxy();
      const { players_stats } = await proxy.getStatisticPlayers(getters.self?.id);
      const data = {
        ...getters.statistic,
        players: {
          loading: false,
          items: StatisticTransform.players(players_stats),
        },
      }
      commit(types.SET_STATISTICS, data);
    } catch (e) {
      commit(types.SET_PLAYERS_STATISTICS_LOADING, false);
      dispatch('notification/error', 'errormessage.failed_to_get_players_statistic', { root: true });
    }
  },

  resetDetect({ dispatch, getters }) {
    if (!getters.detectCurrent) {
      return;
    }

    getters.detectCurrent.isNew
      ? dispatch('deleteNewDetect')
      : dispatch('resetDetectEditing');
  },

  resetDetectEditing({ commit }) {
    commit(types.RESET_DETECT_FOR_EDITING);
  },

  resetVisibilityAllDetects({ commit, dispatch, getters }) {
    if (!getters.showOnlyOneDetect.length) {
      return;
    }

    commit(types.SET_SHOW_ONLY_DETECT, []);
    dispatch('setFilteredDetects');
  },

  async refreshDetects({ dispatch }, payload) {
    dispatch('resetDetectEditing');
    await dispatch('getObjects', payload);
  },

  setDetectEditing({ commit }, payload) {
    commit(types.SET_DETECT_FOR_EDITING, payload);
  },

  setEventsPage({ commit }, payload) {
    commit(types.SET_EVENTS_PAGE, payload);
  },

  setDetectTypes({ commit, dispatch }, payload) {
    commit(types.SET_DETECTS_TYPE, payload);
    dispatch('setFilteredDetects');
  },

  setInterpolatedVisibility({ commit, dispatch }, payload) {
    commit(types.SET_INTERPOLATED_VISIBILITY, payload);
    dispatch('setFilteredDetects');
  },

  toggleVisibilityAllDetects({ commit, dispatch, getters }, payload) {
    const isHas = getters.showOnlyOneDetect.includes(payload);
    const result = isHas
      ? getters.showOnlyOneDetect.filter(el => el !== payload)
      : [ ...getters.showOnlyOneDetect, payload ];

    commit(types.SET_SHOW_ONLY_DETECT, result);
    dispatch('setFilteredDetects');
  },

  setFilteredDetects({ commit, getters }) {
    const { detectTypes, detects, showInterpolatedDetects, showOnlyOneDetect } = getters;
    const filteredDetects = detects
      .reduce((res, current) => {
        // Filter by interpolation
        if (current.interpolation && !showInterpolatedDetects) {
          return res;
        };

        // Filter by object type
        const isShow = current.track_id
          ? detectTypes.includes(current.object_type_id)
          : detectTypes.includes(PLAYERS_ALL_CODE);

        if (!isShow) return res;

        // Set object visibility
        const id = current.track_id || current.id;
        res.push({
          ...current,
          isHide: !!showOnlyOneDetect.length ? !showOnlyOneDetect.includes(id) : false,
        });
        return res;
      }, [])
      .sort((a, b) => {
        if(a.track_id === "") {
          return 1;
        } else if(b.track_id === "") {
          return -1;
        } else {
          return a.track_id > b.track_id ? 1 : -1;
        }
      });
    commit(types.SET_FILTERED_DETECTS, filteredDetects);
  },

  setPreviousNewDetect({ commit }, payload) {
    commit(types.SET_PREVIOUS_NEW_DETECT, payload);
  },

  setTeamShapesVisibility({ commit }, payload) {
    commit(types.SET_TEAM_SHAPES_VISIBILITY, payload);
  },

  setTeams({ commit }, payload) {
    commit(types.TEAMS, payload);
  },

  setKits({ commit }, payload) {
    commit(types.KITS, payload);
  },

  async getObjects({ dispatch }, payload) {
    const { isSetVisible = true, type, ...options } = payload;
    const field = `${payload.frame_id}_getObjects`;
    dispatch('setLoading', { type, field });
    const proxy = new ProjectsProxy();
    try {
      const response = await proxy.getObjects(options);
      const frames = response.frames.flat();
      const detects = DataTransform.fetchCollection(frames);

      if (isSetVisible) {
        dispatch('setVisible', detects);
      }
      return detects;
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_frames_data', { root: true });
    } finally {
      dispatch('setLoading', { type, field });
    }
  },

  selectObject({ commit, getters }, payload) {
    const selected = getters.selectedObject;
    if (selected && selected.id === payload.id) {
      commit(types.RESET_OBJECT);
    } else {
      commit(types.SET_OBJECT, payload);
    }
  },

  setVisible({ commit, dispatch }, payload) {
    commit(types.OBJECTS, payload);
    dispatch('setFilteredDetects');
  },

  setCurrentFrame({ commit }, payload) {
    commit(types.SET_CURRENT_FRAME, payload);
  },

  setAction({ commit }, payload) {
    commit(types.SET_ACTION, payload);
  },

  cancelAction({ commit }) {
    commit(types.SET_ACTION, 'default');
  },

  async getImgDynamic({ commit, dispatch, getters }, payload) {
    const { type, ...options } = payload;
    const prevImg = getters.canvasImage(type);
    const prevFrame = prevImg?.src.split('/frames/')[1];
    if (prevFrame === payload.frame_id.toString()) {
      return
    }

    const field = `${payload.frame_id}_getImg`;
    dispatch('setLoading', { type, field });

    try {
      const proxy = new ImageProxy();
      const img = await proxy.fetch(options);
      commit(types.SET_BACKGROUND, { img, type });
      dispatch('canvasSize', {width: img.width, height: img.height, type});
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_frame_data', { root: true });
    } finally {
      dispatch('setLoading', { type, field });
    }
  },

  async fetchTvData({ dispatch }, payload) {
    await dispatch('getImgDynamic', payload);
  },

  async fetchFieldPoints({ dispatch }, payload) {
    try {
      const { frame_id, job_id } = payload;
      const proxy = new ProjectsProxy();
      const { points = [] } = await proxy.getFieldPoints({ frame_id, job_id });
      return DataTransform.fieldCollection(points);
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_field_data', { root: true });
    }
  },

  async getFieldPointsCanonical({ dispatch }, payload) {
    const { frame_id, job_id, type } = payload;
    const field = `${frame_id}_getObjects`;
    dispatch('setLoading', { type, field });
    try {
      const proxy = new ProjectsProxy();
      const { points = [] } = await proxy.getFieldPointsCanonical({ job_id });
      return DataTransform.fieldCollection(points);
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_field_data', { root: true });
    } finally {
      dispatch('setLoading', { type, field });
    }
  },

  async fetchFrameData({ commit, dispatch, getters }, payload = {}) {
    const { frame_id, job_id, type } = payload;
    const isExistDetect = getters.detects.some(item => item.frame_id === frame_id);
    const isExistField = getters.fieldId === frame_id;

    if (isExistDetect) {
      commit(types.SET_FILTERED_DETECTS, []);
    }
    if (isExistField) {
      commit(types.RESET_FIELD_DATA);
    }

    const field = `${payload.frame_id}_fetchFrameData`;
    const isOpenCanonicalCanvas = getters.canvasIsShow(CANVAS_CANONICAL);
    if (isOpenCanonicalCanvas) {
      dispatch('setLoading', { type: CANVAS_CANONICAL, field });
    }
    const [_, frames, points] = await Promise.all([
      dispatch('getImgDynamic', payload),
      isExistDetect ? null : dispatch('getObjects', { job_id, frame_id, isSetVisible: false, type }),
      isExistField ? null : dispatch('fetchFieldPoints', payload),
    ]);

    if (points) {
      const field = {
        ...getters.field,
        id: frame_id,
        points: points,
      };
      commit(types.SET_FIELD_DATA, field);
    }

    if (isOpenCanonicalCanvas) {
      dispatch('setLoading', { type: CANVAS_CANONICAL, field });
    }

    dispatch('setVisible', frames || getters.detects);
  },

  async fetchCanonicalData({ commit, dispatch, getters }, payload = {}) {
    const { frame_id, job_id, type } = payload;
    const isDetectsAlreadyСalled = getters.canvasIsShow(CANVAS_MAIN);
    const isHaveDetects = getters.detects.some(item => item.frame_id === frame_id);
    const isExistDetect = isDetectsAlreadyСalled || isHaveDetects;
    const isExistFieldCanonical = !!getters.fieldPointsCanonical.length;

    dispatch('setCanonicalParams', payload);
    const [frames, pointsCanonical] = await Promise.all([
      isExistDetect ? null : dispatch('getObjects', { job_id, frame_id, isSetVisible: false, type }),
      isExistFieldCanonical ? null : dispatch('getFieldPointsCanonical', payload),
    ]);

    if (pointsCanonical) {
      const field = {
        ...getters.field,
        id: null,
        pointsCanonical: pointsCanonical,
      };
      commit(types.SET_FIELD_DATA, field);
    }

    dispatch('setVisible', frames || getters.detects);
  },

  async fetchFrameAndObjects({ dispatch }, payload) {
    const { type } = payload;
    if (type === CANVAS_MAIN) {
      return dispatch('fetchFrameData', payload);
    }
    if (type === CANVAS_CANONICAL) {
      return dispatch('fetchCanonicalData', payload);
    }
    if (type === CANVAS_SECOND) {
      return dispatch('fetchTvData', payload);
    }
  },

  reset({ commit }) {
    commit(types.RESET);
  },

  setCanonicalParams({ dispatch, getters }, payload) {
    const { type } = payload;
    const size = getters.canvasSize(CANVAS_CANONICAL);
    if (!!size.width && !!size.height) {
      return
    }

    const width = (getters.self?.field?.length || 0) * 10;
    const height = (getters.self?.field?.width || 0) * 10;
    dispatch('canvasSize', { width, height, type });
  },

  setDetectLabels({ commit }, payload) {
    commit(types.SET_DETECT_LABELS, payload);
  },

  setLoading({ commit, getters }, payload) {
    const { type = CANVAS_MAIN, field } = payload;
    const isHas = getters.loading(type).includes(field);
    const loading = isHas
      ? getters.loading(type).filter(el => el !== field)
      : [...getters.loading(type), field];

    commit(types.LOADING, { type, loading });
  },

  setView({ commit }, payload) {
    commit(types.SET_VIEW, payload);
  },

  setVisibilityCanvas({ commit }, payload) {
    for (let key in payload) {
      commit(types.SET_VISIBILITY_CANVAS, {
        type: key,
        value: payload[key],
      });
    }
  },

  setVisibilityFieldLabel({ commit }, payload) {
    commit(types.SET_FIELD_LABEL_VISIBILITY, payload);
  },

  setVisibilityLineups({ commit }, payload) {
    commit(types.SET_VISIBILITY_LINEUPS, payload);
  },
};
